<template>
  <div>
    <a-modal
      v-model="visible"
      title="Edit Homework"
      :width="isMobileView ? 480 : 720"
      :destroyOnClose="true"
      :maskClosable="false"
      @ok="handleEdit"
    >
      <template slot="footer">
        <a-button key="back" @click="handleCancel">Cancel</a-button>
        <a-button
          key="submit"
          type="primary"
          :loading="loading"
          @click="handleEdit"
          >Update Homework</a-button
        >
      </template>
      <a-form class="mb-4" :form="form">
        <a-form-item label="Title">
          <a-input
            placeholder="title"
            v-decorator="[
              'title',
              {
                rules: [
                  { required: true, message: 'Please input homework title!' },
                  { max: 30, message: 'Must not exceed 30 characters!' },
                ],
              },
            ]"
          />
        </a-form-item>
        <a-form-item label="Description">
          <a-textarea
            placeholder="Description"
            :autoSize="{ minRows: 2 }"
            v-decorator="[
              'description',
              {
                rules: [
                  { required: false, message: 'Please input a description!' },
                ],
              },
            ]"
          />
        </a-form-item>

        <p class="font-weight-bold mt-3">Homework Targets</p>
        <a-divider />

        <div class="row justify-content-center mt-3">
          <!-- Session -->
          <div class="col-md-4 col-12 mb-2">
            <p class="font-weight-bold text-dark mb-0">Session</p>
            <a-select
              size="default"
              v-model="selectedSession"
              optionFilterProp="children"
              style="width: 100%"
              @change="handleSessionChange"
              :filterOption="false"
              :defaultValue="currentSession ? currentSession.id : undefined"
            >
              <template v-for="schoolSession in schoolSessions">
                <a-select-option
                  :value="schoolSession.id"
                  :key="schoolSession.id"
                >
                  {{ schoolSession.name }}
                  <small class="text-primary ml-2">{{
                    currentSession.id === schoolSession.id ? "Current" : ""
                  }}</small>
                </a-select-option>
              </template>
            </a-select>
          </div>
          <!-- Class -->
          <div class="col-md-4 col-12 mb-2">
            <p class="font-weight-bold text-dark mb-0">Class</p>
            <a-spin :spinning="classSpinning">
              <a-select
                size="default"
                v-model="selectedClass"
                placeholder="Select class"
                optionFilterProp="children"
                style="width: 100%"
                @change="handleClassChange"
                :filterOption="false"
                :defaultValue="undefined"
              >
                <a-select-option :value="0"> Select Class </a-select-option>
                <template v-for="schoolClass in schoolClasses">
                  <a-select-option
                    :value="schoolClass.id"
                    :key="schoolClass.id"
                  >
                    {{ schoolClass.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
          <!-- Section -->
          <div class="col-md-4 col-12 mb-2">
            <p class="font-weight-bold text-dark mb-0">Section</p>
            <a-spin :spinning="sectionSpinning">
              <a-select
                size="default"
                v-model="selectedSection"
                placeholder="Select section"
                optionFilterProp="children"
                style="width: 100%"
                :filterOption="false"
                :defaultValue="undefined"
                :disabled="!selectedClass"
              >
                <a-select-option :value="0"> Select Section </a-select-option>
                <template v-for="section in sections">
                  <a-select-option :value="section.id" :key="section.id">
                    {{ section.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
        </div>
        <div class="row justify-content-between">
          <div class="col-12 col-md-8">
            <p class="font-weight-bold text-dark mb-0">Subject</p>
            <a-spin :spinning="subjectSpinning">
              <a-select
                size="default"
                v-model="selectedSubject"
                optionFilterProp="children"
                style="width: 100%"
                :filterOption="false"
                :defaultValue="undefined"
                :disabled="!selectedClass"
              >
                <a-select-option :value="0" disabled
                  >Select Subject</a-select-option
                >
                <a-select-option
                  v-for="subject in subjects"
                  :value="subject.id"
                  :key="subject.id"
                >
                  {{ subject.name }}
                </a-select-option>
              </a-select>
            </a-spin>
          </div>
          <div class="col-12 col-md-4">
            <a-form-item label="Due Date">
              <a-date-picker
                v-decorator="[
                  'due_date',
                  {
                    rules: [
                      { required: false, message: 'Please input due date' },
                    ],
                    initialValue: moment(),
                  },
                ]"
                :format="dateFormat"
              />
            </a-form-item>
          </div>
        </div>

        <p class="font-weight-bold mb-2 mt-3">Homework Files</p>
        <a-divider />

        <div class="w-25">
          <a-form-item class="mt-3">
            <a-upload
              v-decorator="[
                'file',
                {
                  rules: [
                    { required: false, message: 'Please input homework file.' },
                  ],
                },
              ]"
              accept=".jpg,.jpeg,.png,.pdf,.xls,.xlsx,.doc,.docx, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              :fileList="fileList"
              :multiple="false"
              :remove="handleRemove"
              :beforeUpload="beforeUpload"
              :disabled="uploadDisabled"
            >
              <button type="button" class="btn btn-dark btn-with-addon">
                <span class="btn-addon">
                  <i class="btn-addon-icon fe fe-file-text" />
                </span>
                Attach File
              </button>
            </a-upload>
          </a-form-item>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'

export default {
  name: 'EditHomework',
  data: function () {
    return {
      form: this.$form.createForm(this),
      visible: false,
      loading: false,
      oldrecord: {},
      newrecord: {},
      schoolClasses: [],
      sections: [],
      subjects: [],
      classSubjects: [],
      schoolSessions: [],
      selectedClass: null,
      selectedSection: null,
      selectedSubject: null,
      selectedSession: undefined,
      sectionSpinning: false,
      subjectSpinning: false,
      classSpinning: false,
      uploadDisabled: false,
      fileList: [],
      filesToDelete: [],
      dateFormat: 'DD-MM-YYYY',
    }
  },
  mounted: function () {
    this.getSession()
    this.getClasses()
    this.$eventBus.$on('openEditForm', this.showForm)
  },
  computed: {
    ...mapState(['settings', 'user']),
    isMobileView() {
      return this.settings.isMobileView
    },
    currentSession() {
      return this.schoolSessions.find(session => session.is_current === 1)
    },
  },
  methods: {
    moment,
    getSession: function () {
      this.$http
        .get('/api/session')
        .then(({ data }) => {
          this.schoolSessions = data.data
          this.getClasses()
        })
        .catch(this.handleError)
    },
    getClasses: function () {
      this.classSpinning = true
      this.$http
        .get('/api/class', {
          params: {
            session_id: this.selectedSession,
          },
        })
        .then(({ data }) => {
          this.schoolClasses = data.data
          this.classSpinning = false
        })
        .catch(this.handleError)
    },
    handleClassChange(value) {
      this.selectedSection = 0
      this.selectedSubject = 0
      if (value !== null) {
        this.selectedClass = value
        this.sectionSpinning = true
        this.subjectSpinning = true
        this.getClassSections(value)
        this.getClassSubject(value)
      }
    },
    getClassSections(value) {
      this.sectionSpinning = true
      this.$http
        .get(`/api/section/class/${value}`)
        .then(({ data }) => {
          this.sections = data.data
          this.sectionSpinning = false
        })
        .catch(this.handleError)
    },
    getClassSubject(value) {
      this.subjectSpinning = true
      this.$http
        .get(`/api/subject/class/${value}`)
        .then(({ data }) => {
          this.subjects = data.data.normal_subjects
          this.subjectSpinning = false
        })
        .catch(this.handleError)
    },
    handleSessionChange(value) {
      if (value) {
        this.selectedSession = value
      } else {
        this.selectedSession = this.currentSession
      }
      this.selectedClass = 0
      this.selectedSection = 0
      this.selectedSubject = 0
      this.getClasses()
    },

    showForm: function (record) {
      this.visible = true
      this.oldrecord = record
      this.getClassSections(record.class_id)
      this.getClassSubject(record.class_id)
      setTimeout(() => {
        this.form.setFieldsValue({
          title: record.title,
          description: record.description,
          class_id: record.class_id,
          section_id: record.section_id,
          subject_id: record.subject_id,
          due_date: moment(record.due_date),
          files: this.fileList,
        })
        this.selectedSession = record.session_id
        this.selectedClass = record.class_id
        this.selectedSection = record.section_id
        this.selectedSubject = record.subject_id
        if (record.homeworkfiles && record.homeworkfiles.length > 0) {
          this.fileList = []
          record.homeworkfiles.forEach(file => {
            this.fileList.push({
              uid: file.id,
              name: file.original_file_name,
            })
          })
        } else {
          this.fileList = []
        }
      }, 50)
    },
    handleCancel(e) {
      this.visible = false
      this.form.resetFields()
    },
    handleEdit(e) {
      this.loading = true
      this.hasTargetError = false
      this.hasTypeError = false
      this.form.validateFields((err, values) => {
        if (!err) {
          this.$confirm({
            title: `Are you sure you want to send ${values.title}?`,
            content: `Click on the OK button to send a homework!`,
            onOk: () => {
              const formBody = {
                title: values.title,
                description: values.description,
                class_id: this.selectedClass,
                section_id: this.selectedSection,
                subject_id: this.selectedSubject,
                due_date: moment(values.due_date).format('YYYY-MM-DD'),
                files: this.fileList,
                files_to_delete: this.filesToDelete,
              }
              const formData = window.jsonToFormData(formBody)
              this.$http
                .post(`/api/homework/${this.oldrecord.id}`, formData)
                .then(({ data }) => {
                  this.$emit('updateTable')
                  this.loading = false
                  this.visible = false
                  this.$notification['success']({
                    message: 'Homework sent',
                    description: `Homework sent successfully!`,
                  })
                  this.form.resetFields()
                  this.selectedClass = 0
                  this.selectedSection = 0
                  this.selectedSubject = 0
                  this.fileList = []
                })
                .catch(this.handleError)
            },
            onCancel: () => {
              this.loading = false
            },
          })
        } else {
          this.loading = false
          this.form.setFields(err)
        }
        this.loading = false
      })
    },
    handleRemove(file) {
      const index = this.fileList.indexOf(file)
      const newFileList = this.fileList.slice()
      newFileList.splice(index, 1)
      this.fileList = newFileList
      this.filesToDelete.push(file.uid)
      this.uploadDisabled = false
    },
    beforeUpload(file) {
      this.fileList = [...this.fileList, file]
      const isJPG = file.type === 'image/jpeg'
      const isPNG = file.type === 'image/png'
      const isPDF = file.type === 'application/pdf'
      const isMsExcel =
        file.type === 'application/vnd.ms-excel' ||
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const isMsWordDoc =
        file.type === 'application/msword' ||
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      if (!(isJPG || isPNG || isPDF || isMsExcel || isMsWordDoc)) {
        this.$message.error(
          'You can upload only JPG, PNG, Excel, MS Word or PDF file!'
        )
        this.handleRemove(file)
      }
      const isLt2M = file.size / 1024 / 1024 < 10
      if (!isLt2M) {
        this.$message.error('File must smaller than 5MB!')
        this.handleRemove(file)
      }
      return false
    },
    handleError(error) {
      console.error(error)
      this.$notification['error']({
        message: 'Error',
        description: error.message,
      })
    },
  },
}
</script>
